<template>
  <div class="image-cropper-container">
    <!-- Image Upload Area -->
    <div v-if="!selectedImage && !croppedImage" class="image-upload-area" 
         @click="$refs.fileInput.click()"
         @dragover.prevent
         @drop.prevent="handleDrop">
      <input 
        ref="fileInput"
        type="file" 
        @change="handleFileSelect"
        accept="image/*"
        class="d-none">
      <div class="upload-placeholder">
        <div class="upload-icon">
          <i class="fe fe-upload-cloud"></i>
        </div>
        <div class="upload-text">Drag and Drop the Image</div>
        <div class="upload-subtext">Upload landscape image (Max. Size 2MB)</div>
        <button type="button" class="choose-file-btn">Choose File</button>
      </div>
    </div>

    <!-- Cropper Interface -->
    <div v-else-if="selectedImage && !croppedImage" class="cropper-container">
      <div class="cropper-header d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Crop Image</h5>
        <button type="button" class="btn btn-sm btn-secondary" @click="cancelCrop">
          <i class="fe fe-x"></i> Cancel
        </button>
      </div>
      
      <div class="cropper-wrapper">
        <cropper
          ref="cropper"
          class="cropper"
          :src="selectedImage"
          :stencil-props="stencilProps"
          @change="onCropChange"
        />
      </div>

      <div class="cropper-controls mt-3">
        <div class="row">
          <!-- <div class="col-md-6">
            <label class="form-label">Aspect Ratio (Landscape):</label>
            <select v-model="selectedAspectRatio" @change="changeAspectRatio" class="form-select form-select-sm">
              <option value="1.777">16:9 (Recommended)</option>
              <option value="1.333">4:3</option>
              <option value="2">2:1</option>
              <option value="1.5">3:2</option>
              <option value="">Free (Landscape)</option>
            </select>
          </div> -->
          <div class="col-md-6 d-flex align-items-end">
            <button type="button" class="btn btn-sm btn-primary" @click="cropImage">
              <i class="fe fe-crop"></i> Crop Image
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Cropped Result -->
    <div v-else-if="croppedImage" class="cropped-result">
      <div class="cropped-header d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Cropped Image</h5>
        <div>
          <button v-if="isCropped" type="button" class="btn btn-sm btn-outline-secondary me-2" @click="resetToCrop">
              <i class="fe fe-refresh-cw"></i> Reset
            </button>
          <button type="button" class="btn btn-sm btn-danger" @click="removeCrop">
            <i class="fe fe-trash"></i> Remove
          </button>
        </div>
      </div>
      
      <div class="cropped-image-container">
        <img :src="croppedImage" alt="Cropped" class="img-fluid rounded">
      </div>
    </div>
  </div>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

export default {
  name: 'ImageCropper',
  components: {
    Cropper
  },
  props: {
    value: {
      type: File,
      default: null
    },
    previewUrl: {
      type: String,
      default: null
    },
    aspectRatio: {
      type: Number,
      default: null
    },
    maxFileSize: {
      type: Number,
      default: 2 * 1024 * 1024 // 2MB
    }
  },
  data() {
    return {
      selectedImage: null,
      croppedImage: null,
      croppedFile: null,
      originalImage: null, // Store original image for edit
      originalFile: null, // Store original file for immediate use
      selectedAspectRatio: this.aspectRatio ? String(this.aspectRatio) : '1.777', // Default to 16:9
      coordinates: null,
      isCropped: false // Track if image has been cropped
    }
  },
  computed: {
    stencilProps() {
      const ratio = this.selectedAspectRatio ? parseFloat(this.selectedAspectRatio) : 1.777
      // Ensure landscape orientation (ratio > 1)
      const landscapeRatio = ratio >= 1 ? ratio : 1.777
      
      return {
        aspectRatio: landscapeRatio,
        handlers: {},
        movable: true,
        resizable: true
      }
    }
  },
  watch: {
    previewUrl: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.croppedImage = newVal
          this.selectedImage = null
          this.isCropped = true // previewUrl means image already exists
          // Use nextTick to ensure component is mounted before emitting
          this.$nextTick(() => {
            this.$emit('crop-status', true) // Existing image = no crop needed
          })
        }
      }
    },
    value: {
      immediate: true,
      handler(newVal) {
        // Only reset if value is null AND there's no original image being worked on
        if (!newVal && !this.originalImage && !this.selectedImage) {
          this.resetAll()
        }
      }
    }
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.processFile(file)
      }
    },

    handleDrop(event) {
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.processFile(file)
      }
    },

    processFile(file) {
      // Validate file size
      if (file.size > this.maxFileSize) {
        this.$emit('error', `File size must be less than ${this.formatFileSize(this.maxFileSize)}`)
        return
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.$emit('error', 'Please select a valid image file')
        return
      }

      // Store original file for immediate use (before cropping)
      this.originalFile = file
      this.isCropped = false

      // Emit null first - file will only be available after cropping
      this.$emit('input', null)
      this.$emit('change', null)
      this.$emit('crop-status', false) // Notify parent that crop is required

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        this.selectedImage = e.target.result
        this.originalImage = e.target.result // Store original
        this.croppedImage = null
        this.croppedFile = null
      }
      reader.readAsDataURL(file)
    },

    onCropChange({ coordinates, canvas }) {
      this.coordinates = coordinates
    },

    changeAspectRatio() {
      const ratio = this.selectedAspectRatio ? parseFloat(this.selectedAspectRatio) : 1.777
      // Ensure landscape orientation (ratio > 1)
      const landscapeRatio = ratio >= 1 ? ratio : 1.777
      
      this.$emit('aspect-ratio-change', landscapeRatio)
      
      // Update cropper stencil with landscape proportions
      if (this.$refs.cropper) {
        this.$refs.cropper.setCoordinates({
          width: 400,
          height: 400 / landscapeRatio,
          left: 50,
          top: 50
        })
      }
    },

    resetCrop() {
      if (this.$refs.cropper) {
        this.$refs.cropper.reset()
      }
    },

    async cropImage() {
      if (!this.$refs.cropper) return

      const { canvas } = this.$refs.cropper.getResult()
      if (!canvas) return

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Create file from blob
          const fileName = `cropped_image_${Date.now()}.jpg`
          const croppedFile = new File([blob], fileName, {
            type: 'image/jpeg',
            lastModified: Date.now()
          })

          // Create preview URL
          this.croppedImage = canvas.toDataURL('image/jpeg', 0.9)
          this.croppedFile = croppedFile
          this.isCropped = true
          // this.selectedImage = null // Clear selected to show result

          // Emit the cropped file
          this.$emit('input', croppedFile)
          this.$emit('change', croppedFile)
          this.$emit('crop-status', true) // Notify parent that crop is done
          this.$emit('cropped', {
            file: croppedFile,
            preview: this.croppedImage
          })
        }
      }, 'image/jpeg', 0.9)
    },

    editCrop() {
      this.croppedImage = null
      this.croppedFile = null
    },

    resetToCrop() {
      // Kembali ke cropper interface dengan gambar original
      if (this.originalImage) {
        // Set selectedImage first before clearing croppedImage
        const tempOriginal = this.originalImage
        
        this.croppedImage = null
        this.croppedFile = null
        this.isCropped = false
        
        // Use nextTick to ensure Vue updates the DOM properly
        this.$nextTick(() => {
          this.selectedImage = tempOriginal
          this.$emit('crop-status', false) // Notify parent that crop is required again
        })
      }
    },

    cancelCrop() {
      this.resetAll()
      this.$emit('crop-status', true) // Cancelled = no image = no crop needed
    },

    removeCrop() {
      this.resetAll()
      this.$emit('input', null)
      this.$emit('change', null)
      this.$emit('crop-status', true) // No image = no crop needed
      this.$emit('removed')
    },

    resetAll() {
      this.selectedImage = null
      this.croppedImage = null
      this.croppedFile = null
      this.originalImage = null
      this.originalFile = null
      this.coordinates = null
      this.isCropped = false
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    // Method to check if image is cropped (can be called from parent via ref)
    isImageCropped() {
      return this.isCropped
    },

    // Method to check if image is selected but not cropped
    needsCrop() {
      return this.selectedImage !== null && !this.isCropped
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.image-cropper-container {
  width: 100%;
}

.image-upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
}

.image-upload-area:hover {
  border-color: #007bff;
  background-color: #f0f7ff;
}

.upload-placeholder {
  color: #6c757d;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 16px;
  color: #007bff;
}

.upload-text {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
}

.upload-subtext {
  font-size: 14px;
  margin-bottom: 20px;
}

.choose-file-btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.choose-file-btn:hover {
  background-color: #0056b3;
}

.cropper-container {
  width: 100%;
}

.cropper-wrapper {
  height: 400px;
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
}

.cropper {
  height: 100%;
  width: 100%;
}

.cropper-controls {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.cropped-result {
  width: 100%;
}

.cropped-image-container {
  max-height: 400px;
  overflow: hidden;
  text-align: center;
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.cropped-image-container img {
  max-height: 360px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Cropper theme customization */
:deep(.vue-advanced-cropper__background) {
  background: #f8f9fa;
}

:deep(.vue-advanced-cropper__foreground) {
  border-radius: 8px;
}

:deep(.vue-advanced-cropper__stencil) {
  border: 2px solid #007bff;
}

:deep(.vue-advanced-cropper__handler) {
  background: #007bff;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

:deep(.vue-advanced-cropper__line) {
  border-color: #007bff;
}
</style>